/**
 * SEO cluster: CV with ChatGPT / Gemini / Claude (ES + EN).
 * No imports; merged into the blog catalog from posts.js.
 */

export default [
  {
    slug: 'como-hacer-un-cv-con-chatgpt',
    date: '2026-08-17',
    category: 'ai',
    related: ['prompts-ia-para-cv', 'chatgpt-o-clarcv-para-tu-cv', 'privacidad-ia-cv'],
    es: {
      title: 'Cómo hacer un CV con ChatGPT (paso a paso)',
      description:
        'Método práctico para redactar un currículum con ChatGPT o Gemini: inventario, prompts, revisión de inventos y por qué el chat no sustituye un PDF ATS.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'Si buscas cómo hacer un CV con ChatGPT, el error más caro es pedirle “escríbeme un currículum increíble” y pegar el resultado en un Word. El modelo no conoce tu trayectoria, no ve la oferta y no genera un archivo que los filtros ATS parseen bien. Puede ayudarte a redactar. No puede sustituir hechos, formato ni una última lectura humana.',
        },
        {
          type: 'p',
          text: 'Esta guía describe un método que sí funciona: primero un inventario de pruebas, después un prompt con oferta y CV actual, luego una pasada para cazar métricas inventadas y lenguaje genérico. El mismo flujo sirve en Gemini y en Claude. Al final, el texto tiene que vivir en un PDF con texto seleccionable, secciones estándar y keywords verdaderas. Ahí es donde un chat se queda corto y una herramienta como ClarCV (clarcv.com) cierra el círculo.',
        },
        {
          type: 'p',
          text: 'No vas a “hackear” ChatGPT. Vas a usarlo como un redactor rápido al que hay que supervisar. Si hoy no tienes CV, el inventario te da materia prima. Si ya tienes un PDF flojo, no lo tires: mejóralo. Empezar de cero en el chat suele producir un documento que parece profesional y se desmonta en la entrevista.',
        },
        {
          type: 'h2',
          text: 'Qué hace bien ChatGPT (y qué no)',
        },
        {
          type: 'p',
          text: 'ChatGPT es útil para convertir tareas en logros, acortar un resumen, proponer verbos de acción y alinear el vocabulario con una oferta. También traduce un borrador al inglés con más fluidez que un traductor palabra a palabra. Gemini y Claude rinden parecido si les das el mismo contexto: CV + anuncio + reglas claras.',
        },
        {
          type: 'ul',
          items: [
            'Bien: reescribir viñetas con verbo, alcance y resultado a partir de datos que tú aportas.',
            'Bien: extraer 8–15 keywords de una oferta y señalar cuáles ya están en tu CV.',
            'Bien: acortar un resumen a 3–4 líneas orientadas al rol.',
            'Mal: inventar porcentajes, equipos o herramientas que no usaste.',
            'Mal: exportar un PDF ATS, elegir plantilla parseable o analizar el parseo del archivo.',
            'Mal: conocer tu mercado mejor que tú si no le pegas la oferta real.',
          ],
        },
        {
          type: 'p',
          text: 'El modelo completa patrones. Si le das poco contexto, rellena con clichés: “proactivo”, “orientado a resultados”, “excelentes habilidades de comunicación”. Eso no pasa filtros ni convence a un reclutador. La calidad del CV de ChatGPT es la calidad de lo que le pegas y de lo que tachas después.',
        },
        {
          type: 'h2',
          text: 'Paso 1: inventario (15 minutos, sin el chat)',
        },
        {
          type: 'p',
          text: 'Abre un documento en blanco. Lista puestos (título, empresa, mes/año), 3–5 hechos por rol (aunque estén mal escritos), herramientas reales, formación, idiomas y enlaces. Incluye proyectos, prácticas y voluntariado si eres junior. No diseñes. No pidas aún a la IA que “invente impacto”.',
        },
        {
          type: 'p',
          text: 'Añade números honestos: tamaño de cartera, frecuencia, personas en el equipo, volumen de tickets, presupuesto aproximado, plazos. Si no tienes KPI, usa proxies: “unas 25 llamadas al día”, “inventario de 300 productos”. En la entrevista tendrás que defender cada cifra. ChatGPT no irá contigo.',
        },
        {
          type: 'ul',
          items: [
            'Una familia de rol objetivo (por ejemplo Customer Success B2B, no “lo que salga”).',
            'Una o dos ofertas reales pegadas en texto, no un recuerdo vago del anuncio.',
            'Lo que vas a eliminar: hobbies irrelevantes, cursos muertos, soft skills sin prueba.',
            'Datos que no debes pegar en chatgpt.com: DNI, dirección exacta, foto, contraseñas.',
          ],
        },
        {
          type: 'h2',
          text: 'Paso 2: el prompt que sí funciona en ChatGPT',
        },
        {
          type: 'p',
          text: 'No uses un chat vacío. Pega inventario u CV actual, la oferta y reglas. Un prompt útil en español: Actúa como reclutador en España que también piensa en filtros ATS. Te paso 1) mi CV o inventario 2) una oferta. Reescribe el CV en español, una página si es junior o mid, secciones Contacto, Resumen, Experiencia, Formación, Habilidades, Idiomas. Empieza cada viñeta con un verbo. No inventes métricas, herramientas ni títulos. Si falta un dato, escribe [FALTA] en lugar de rellenar. No uses clichés (proactivo, apasionado, resultados). Devuelve solo el CV en texto, sin diseño.',
        },
        {
          type: 'p',
          text: 'Si ya tienes un PDF, pega el texto (cópialo desde el archivo; si no puedes seleccionar, el ATS tampoco podrá). Luego: “No cambies fechas, empresas ni títulos salvo que yo lo pida. Mejora claridad y alinea keywords verdaderas de la oferta.” Esa restricción reduce alucinaciones.',
        },
        {
          type: 'p',
          text: 'Segunda pasada, en el mismo hilo: “Señala en una lista qué frases suenan genéricas, qué keywords de la oferta faltan y cuáles he inventado yo sin darte datos.” Usa esa lista para editar, no para aceptar el segundo borrador a ciegas. Hay un pack de prompts listos (ChatGPT, Gemini y Claude) en la guía de prompts para CV de este blog.',
        },
        {
          type: 'h2',
          text: 'El mismo método en Gemini (y en Claude)',
        },
        {
          type: 'p',
          text: 'Hacer un currículum con Gemini no es otro sistema: es el mismo inventario, el mismo prompt y la misma revisión. En Gemini puedes adjuntar el PDF si el texto se extrae bien; comprueba que no se coma columnas o el email del encabezado. Claude suele respetar mejor las instrucciones del tipo “no inventes”. Ninguno de los tres produce un diseño ATS por sí solo.',
        },
        {
          type: 'p',
          text: 'Si cambias de herramienta a mitad, vuelve a pegar oferta e inventario. Los chats no comparten memoria entre productos. Tampoco asumas que “GPT-4o” o “Gemini Advanced” detectan mentiras: un modelo avanzado inventa con más fluidez si le dejas huecos.',
        },
        {
          type: 'h2',
          text: 'Paso 3: caza inventos y lenguaje de plantilla',
        },
        {
          type: 'p',
          text: 'Lee cada viñeta en voz alta. Pregunta: ¿puedo contar esta historia en 60 segundos? Si ChatGPT escribió “reduje el churn un 23%” y tú nunca mediste churn, bórralo. Sustituye por un proxy real o por el proceso que sí hiciste. Lo mismo con Salesforce, SAP o Python si solo los viste en un curso de dos horas.',
        },
        {
          type: 'p',
          text: 'Antes (típico de chat): Responsable de mejorar la experiencia del cliente y optimizar procesos con mentalidad data-driven. Después: Gestioné 40 cuentas en HubSpot, bajé el tiempo de primera respuesta de 12 a 5 horas y documenté el onboarding en Notion para el equipo. Misma persona, señal usable. El ATS encuentra HubSpot y Notion; el humano entiende el trabajo.',
        },
        {
          type: 'p',
          text: 'Revisa títulos inflados. “Lead” y “Head of” quedan bien en el chat y mal en una comprobación de LinkedIn. Fechas, empresas y cargos tienen que coincidir con tu perfil público. Un CV de ChatGPT que contradice LinkedIn genera desconfianza inmediata.',
        },
        {
          type: 'h2',
          text: 'Paso 4: el chat no es el archivo que envías',
        },
        {
          type: 'p',
          text: 'Copiar el texto a Canva o a una plantilla de dos columnas y exportar a PDF es el fallo clásico. Muchos de esos PDF son imagen o tablas frágiles. El filtro ATS necesita texto seleccionable, encabezados estándar (Experiencia, Formación, Habilidades) y fechas junto a cada puesto. Haz la prueba: abre el PDF, selecciona tres párrafos y pégalos en el bloc de notas. Si no sale texto, no envíes ese archivo.',
        },
        {
          type: 'p',
          text: 'ChatGPT puede devolverte markdown. Eso no es un currículum. Necesitas un documento A4, nombre de archivo profesional (Ana_Lopez_CSM.pdf) y un peso razonable. Para la mayoría de portales en España, un PDF limpio de una columna es más seguro que un diseño “premium” que se rompe al parsearse.',
        },
        {
          type: 'ul',
          items: [
            'Una columna principal; evita sidebars con skills solo en iconos.',
            'Contacto en texto al inicio (email, teléfono, ciudad), no solo en el encabezado gráfico.',
            'Keywords de la oferta dentro de logros, no repetidas al pie en blanco.',
            'Idioma del anuncio: no mezcles español e inglés en el mismo PDF salvo que lo pidan.',
          ],
        },
        {
          type: 'h2',
          text: 'Privacidad: ChatGPT de consumo no es una herramienta de CV',
        },
        {
          type: 'p',
          text: 'Pegar un CV completo en chatgpt.com es cómodo y arriesgado. Un currículum lleva nombre, teléfono, email, a veces foto y trayectoria detallada. En el ChatGPT de consumo, el tratamiento de datos depende de tu cuenta y de los ajustes; no es el mismo marco que una API de empresa con contrato de encargado. No pegues DNI, dirección exacta ni documentos de identidad. Anonimiza el contacto si solo quieres ayuda de redacción.',
        },
        {
          type: 'p',
          text: 'En ClarCV el CV no se pega en chatgpt.com: viaja por API de OpenAI para tu análisis o reescritura, sin uso para entrenar modelos de propósito general, con DPA. Eso no significa “no se almacena en ningún servidor”: hace falta cuenta y archivo para editar y descargar. Significa que no vendemos tus datos ni los usamos para publicidad. Detalle en la guía de privacidad e IA de este blog y en clarcv.com/privacy.',
        },
        {
          type: 'h2',
          text: 'Cuándo pasar de ChatGPT a ClarCV',
        },
        {
          type: 'p',
          text: 'Usa el chat para el borrador de texto. Usa ClarCV cuando necesites el siguiente paso: subir el PDF o Word, recibir análisis de claridad y estructura, y regenerar una versión con plantillas pensadas para ATS e impresión A4. Empieza gratis; Pro son 7 €/mes si iteras a menudo. Interfaz en español e inglés; el CV de salida puede seguir el idioma de cada oferta.',
        },
        {
          type: 'p',
          text: 'El atajo honesto: inventario → prompt con oferta → cazar inventos → subir a ClarCV para formato parseable. Saltar el inventario y pedirle a ChatGPT un CV “de senior data-driven” es la forma más rápida de un documento que parece IA y no consigue entrevistas.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Puedo hacer un CV entero solo con ChatGPT?',
              a: 'Puedes redactar el texto. El archivo que envías (PDF seleccionable, secciones, plantilla) y la veracidad de cada logro siguen siendo tuyos. Sin oferta pegada, el resultado suele ser genérico.',
            },
            {
              q: '¿Gemini es mejor que ChatGPT para currículums?',
              a: 'No de forma mágica. Gana quien recibe inventario + oferta + la regla de no inventar. Gemini conviene si ya trabajas en Google; el método no cambia.',
            },
            {
              q: '¿Debo decirle que actúe como reclutador de Google?',
              a: 'Mejor: reclutador del sector y país de la oferta, más un recordatorio ATS. “Actúa como experto de Fortune 500” añade teatro, no datos.',
            },
            {
              q: '¿El CV de ChatGPT pasa los filtros ATS?',
              a: 'El texto puede incluir keywords. El parseo depende del archivo. Un PDF-imagen o una plantilla de columnas rotas falla aunque el chat haya escrito bien.',
            },
            {
              q: '¿Es seguro pegar mi CV en el chat?',
              a: 'Evita datos sensibles. Para un flujo con API de empresa y política clara, usa un producto como ClarCV en lugar de chatgpt.com.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'How to make a CV with ChatGPT (step by step)',
      description:
        'A practical method to draft a résumé with ChatGPT or Gemini: inventory, prompts, catching invented metrics, and why chat output is not an ATS PDF.',
      readMinutes: 15,
      sections: [
        {
          type: 'p',
          text: 'If you are searching how to make a CV with ChatGPT, the expensive mistake is asking it to “write an amazing résumé” and pasting the result into a document. The model does not know your career, cannot see the vacancy unless you paste it, and does not produce a file that ATS parsers handle well. It can help you write. It cannot replace facts, layout, or a human final pass.',
        },
        {
          type: 'p',
          text: 'This guide is a method that works: first an evidence inventory, then a prompt that includes the job ad and your current CV, then a pass to catch invented metrics and generic phrasing. The same flow works in Gemini and Claude. Finally the text has to live in a selectable-text PDF with standard headings and truthful keywords. That is where a chat falls short and a tool like ClarCV (clarcv.com) finishes the job.',
        },
        {
          type: 'p',
          text: 'You will not “hack” ChatGPT. You will use it as a fast writer you must supervise. If you have no CV yet, the inventory is your raw material. If you already have a weak PDF, do not throw it away—improve it. Starting from a blank chat often yields something that looks professional and collapses in the interview.',
        },
        {
          type: 'h2',
          text: 'What ChatGPT does well (and what it does not)',
        },
        {
          type: 'p',
          text: 'ChatGPT is useful for turning tasks into achievements, shortening a summary, suggesting action verbs, and aligning vocabulary with a posting. It can also produce a more natural English draft than word-for-word translation. Gemini and Claude perform similarly if you give the same context: CV + ad + clear rules.',
        },
        {
          type: 'ul',
          items: [
            'Good: rewriting bullets with verb, scope, and outcome from facts you provide.',
            'Good: extracting 8–15 keywords from an ad and marking which ones already appear in your CV.',
            'Good: cutting a summary to 3–4 lines aimed at the target role.',
            'Bad: inventing percentages, team sizes, or tools you never used.',
            'Bad: exporting an ATS PDF, choosing a parseable template, or checking how the file parses.',
            'Bad: knowing your market better than you do if you do not paste the real posting.',
          ],
        },
        {
          type: 'p',
          text: 'The model completes patterns. With thin context it fills in clichés: proactive, results-oriented, excellent communication skills. That does not pass filters or persuade a recruiter. The quality of a ChatGPT CV is the quality of what you paste and what you delete afterwards.',
        },
        {
          type: 'h2',
          text: 'Step 1: inventory (15 minutes, no chat)',
        },
        {
          type: 'p',
          text: 'Open a blank document. List roles (title, employer, month/year), 3–5 facts per role (even if poorly written), real tools, education, languages, and links. Include projects, internships, and volunteering if you are junior. Do not design. Do not ask the model to invent impact yet.',
        },
        {
          type: 'p',
          text: 'Add honest numbers: book of business, frequency, team size, ticket volume, approximate budget, deadlines. If you lack KPIs, use proxies: “about 25 calls a day”, “inventory of 300 SKUs”. You will have to defend every figure in interview. ChatGPT will not be in the room.',
        },
        {
          type: 'ul',
          items: [
            'One target role family (for example B2B Customer Success, not “whatever comes up”).',
            'One or two real job ads pasted as text, not a vague memory of the posting.',
            'What you will cut: irrelevant hobbies, dead courses, soft skills with no proof.',
            'What not to paste into chatgpt.com: national ID, full street address, photo, passwords.',
          ],
        },
        {
          type: 'h2',
          text: 'Step 2: a ChatGPT prompt that actually works',
        },
        {
          type: 'p',
          text: 'Do not start from an empty chat. Paste inventory or current CV, the ad, and rules. A useful prompt: Act as a recruiter who also thinks about ATS filters. I will paste 1) my CV or inventory 2) a job ad. Rewrite the CV in the language of the ad, one page if junior or mid-level, sections Contact, Summary, Experience, Education, Skills, Languages. Start each bullet with a verb. Do not invent metrics, tools, or job titles. If a fact is missing, write [MISSING] instead of filling the gap. Avoid clichés (proactive, passionate, results-driven). Return CV text only, no layout.',
        },
        {
          type: 'p',
          text: 'If you already have a PDF, paste the text (copy from the file; if you cannot select text, neither can an ATS). Then: “Do not change dates, employers, or titles unless I ask. Improve clarity and align truthful keywords from the ad.” That constraint reduces hallucinations.',
        },
        {
          type: 'p',
          text: 'Second pass in the same thread: “List which sentences sound generic, which ad keywords are missing, and which claims I never gave you data for.” Use that list to edit; do not blindly accept the second draft. A ready-made pack for ChatGPT, Gemini, and Claude is in the AI prompts for CVs guide on this blog.',
        },
        {
          type: 'h2',
          text: 'The same method in Gemini (and Claude)',
        },
        {
          type: 'p',
          text: 'Making a résumé with Gemini is not a different system: same inventory, same prompt, same review. Gemini may let you attach the PDF if extraction works; check that it does not drop columns or the header email. Claude often follows “do not invent” more tightly. None of the three produces an ATS layout on its own.',
        },
        {
          type: 'p',
          text: 'If you switch tools mid-way, paste the ad and inventory again. Chats do not share memory across products. Do not assume a “smarter” model detects lies: a stronger model invents more fluently if you leave gaps.',
        },
        {
          type: 'h2',
          text: 'Step 3: catch inventions and template language',
        },
        {
          type: 'p',
          text: 'Read every bullet out loud. Ask: can I tell this story in 60 seconds? If ChatGPT wrote “reduced churn 23%” and you never measured churn, delete it. Replace with a real proxy or the process you actually ran. Same for Salesforce, SAP, or Python if you only saw them in a two-hour course.',
        },
        {
          type: 'p',
          text: 'Before (typical chat): Responsible for improving customer experience and optimizing processes with a data-driven mindset. After: Managed 40 accounts in HubSpot, cut first-response time from 12 hours to 5, and documented onboarding in Notion for the team. Same person, usable signal. The ATS finds HubSpot and Notion; the human understands the work.',
        },
        {
          type: 'p',
          text: 'Watch inflated titles. “Lead” and “Head of” look fine in chat and bad against a LinkedIn check. Dates, employers, and titles must match your public profile. A ChatGPT CV that contradicts LinkedIn creates instant distrust.',
        },
        {
          type: 'h2',
          text: 'Step 4: the chat is not the file you send',
        },
        {
          type: 'p',
          text: 'Pasting the text into Canva or a two-column template and exporting PDF is the classic failure. Many of those files are images or fragile tables. An ATS needs selectable text, standard headings (Experience, Education, Skills), and dates next to each role. Test: open the PDF, select three paragraphs, paste into a plain-text editor. If nothing comes out, do not send that file.',
        },
        {
          type: 'p',
          text: 'ChatGPT can return markdown. That is not a résumé. You need an A4 document, a professional filename (Ana_Lopez_CSM.pdf), and a reasonable file size. For most European job portals, a clean single-column PDF is safer than a “premium” layout that breaks when parsed.',
        },
        {
          type: 'ul',
          items: [
            'One main column; avoid sidebars where skills exist only as icons.',
            'Contact as real text at the top (email, phone, city), not only in a graphic header.',
            'Ad keywords inside achievements, not repeated in white text at the bottom.',
            'Match the ad language: do not mix Spanish and English in one PDF unless asked.',
          ],
        },
        {
          type: 'h2',
          text: 'Privacy: consumer ChatGPT is not a CV product',
        },
        {
          type: 'p',
          text: 'Pasting a full CV into chatgpt.com is convenient and risky. A résumé carries name, phone, email, sometimes a photo, and a detailed career. In consumer ChatGPT, data handling depends on your account and settings; it is not the same framework as a business API with a processor agreement. Do not paste national ID, a full street address, or identity documents. Strip contact details if you only want writing help.',
        },
        {
          type: 'p',
          text: 'On ClarCV your CV is not pasted into chatgpt.com: it goes through OpenAI’s API for your analysis or rewrite, is not used to train general-purpose models, and is covered by a DPA. That does not mean “stored on no server”: the product needs an account and file so you can edit and download. It means we do not sell your data or use it for ads. Detail in the privacy and AI guide on this blog and at clarcv.com/privacy.',
        },
        {
          type: 'h2',
          text: 'When to move from ChatGPT to ClarCV',
        },
        {
          type: 'p',
          text: 'Use the chat for a text draft. Use ClarCV for the next step: upload PDF or Word, get analysis of clarity and structure, and regenerate a version with templates built for ATS and A4 print. Start free; Pro is €7/month if you iterate often. UI in Spanish and English; output can follow the language of each application.',
        },
        {
          type: 'p',
          text: 'The honest shortcut: inventory → prompt with the ad → catch inventions → upload to ClarCV for a parseable file. Skipping the inventory and asking ChatGPT for a “senior data-driven CV” is the fastest way to a document that looks like AI and does not get interviews.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Can I make a whole CV with ChatGPT alone?',
              a: 'You can draft the wording. The file you send (selectable PDF, sections, template) and the truth of each achievement remain yours. Without a pasted job ad, output is usually generic.',
            },
            {
              q: 'Is Gemini better than ChatGPT for résumés?',
              a: 'Not magically. The winner is whoever gets inventory + ad + a do-not-invent rule. Gemini is convenient if you already work in Google; the method does not change.',
            },
            {
              q: 'Should I tell it to act as a Google recruiter?',
              a: 'Better: a recruiter in the ad’s sector and country, plus an ATS reminder. “Act as a Fortune 500 expert” adds theatre, not facts.',
            },
            {
              q: 'Will a ChatGPT CV pass ATS filters?',
              a: 'The text can include keywords. Parsing depends on the file. An image PDF or a broken column template fails even if the chat wrote well.',
            },
            {
              q: 'Is it safe to paste my CV into the chat?',
              a: 'Avoid sensitive data. For a business-API flow with a clear policy, use a product like ClarCV instead of chatgpt.com.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'prompts-ia-para-cv',
    date: '2026-08-17',
    category: 'ai',
    related: ['como-hacer-un-cv-con-chatgpt', 'chatgpt-o-clarcv-para-tu-cv', 'adaptar-cv-a-una-oferta'],
    es: {
      title: 'Prompts de ChatGPT, Gemini y Claude para hacer un CV',
      description:
        'Prompts listos para copiar: mejorar el CV, adaptarlo a una oferta, reescribir logros y extraer keywords. ChatGPT, Gemini y Claude, con reglas para que no inventen.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Un prompt de ChatGPT para CV no es una frase mágica. Es un briefing: quién eres, qué oferta, qué reglas y qué formato de salida. Si solo escribes “hazme un currículum”, el modelo rellena huecos con clichés y, a menudo, con métricas que nunca te ocurrieron. Esta guía es un pack para copiar, pegar y editar.',
        },
        {
          type: 'p',
          text: 'Sirve en ChatGPT, Gemini y Claude. Cambia poco el texto del prompt; cambia el contexto que pegas debajo. Siempre: inventario o CV actual + oferta en texto + “no inventes”. El método completo (inventario, revisión, PDF ATS) está en cómo hacer un CV con ChatGPT. Aquí nos centramos en las instrucciones.',
        },
        {
          type: 'p',
          text: 'Antes de pegar nada en chatgpt.com, quita DNI, dirección exacta y foto. Si el objetivo es un archivo listo para enviar, el chat redacta; ClarCV formatea y analiza. Los prompts no sustituyen hechos. Si pones [FALTA], rellena tú, no le pidas al modelo que “estime un porcentaje realista”.',
        },
        {
          type: 'h2',
          text: 'Reglas que van en todos los prompts',
        },
        {
          type: 'ul',
          items: [
            'Idioma de salida = idioma de la oferta (español o inglés, no un híbrido).',
            'No inventar métricas, herramientas, títulos, fechas ni empresas.',
            'Si falta un dato, marcar [FALTA] o [MISSING], no rellenar.',
            'Viñetas con verbo + qué + alcance o herramienta + resultado si existe.',
            'Prohibido: proactivo, apasionado, orientado a resultados, excelentes habilidades de comunicación, data-driven como adorno.',
            'No pedir diseño, columnas, iconos ni “CV visual premium”.',
          ],
        },
        {
          type: 'h2',
          text: 'Prompt 1: reescribir el CV que ya tienes',
        },
        {
          type: 'p',
          text: 'Copia esto y pega debajo tu CV en texto y, si puedes, una oferta típica de tu objetivo. Prompt: Eres un editor de currículums en España. Te paso mi CV actual. Mejora claridad y densidad. Conserva fechas, empresas y títulos. Convierte tareas en logros solo cuando yo haya dado un resultado o un proxy. Elimina clichés. Una página si mi trayectoria es corta; si no, indica qué cortarías. Estructura: Resumen (3–4 líneas), Experiencia, Formación, Habilidades, Idiomas. Devuelve solo el CV.',
        },
        {
          type: 'p',
          text: 'Segundo mensaje en el mismo hilo: Haz una tabla breve: frase original → frase nueva → por qué. Si has añadido cualquier número que yo no di, lístalo aparte. Esa tabla es tu control de calidad. ChatGPT y Gemini suelen “mejorar” metiendo cifras redondas; la tabla las delata.',
        },
        {
          type: 'h2',
          text: 'Prompt 2: adaptar el CV a una oferta (el de más impacto)',
        },
        {
          type: 'p',
          text: 'Este es el prompt que más entrevistas mueve, porque fuerza alineación. Prompt: Te paso A) mi CV B) una oferta de empleo. Extrae 10 keywords reales de la oferta (herramientas, dominio, verbos de responsabilidad). Dime cuáles ya están en mi CV y cuáles faltan. Reescribe resumen, las 5 viñetas más relevantes y la lista de skills usando solo keywords que yo pueda defender. No copies requisitos que no cumplo. No inventes la herramienta estrella del anuncio si no la he usado. Salida: 1) lista de keywords 2) CV adaptado 3) tres frases que sigue sin cubrir y cómo las abordaría en entrevista con honestidad.',
        },
        {
          type: 'p',
          text: 'Si postulas a dos familias de rol (por ejemplo operaciones y atención al cliente), no uses el mismo hilo. Empieza un chat nuevo por familia. Mezclar ofertas produce un Frankenstein que no convence a nadie.',
        },
        { type: 'cta', variant: 'upload' },
        {
          type: 'h2',
          text: 'Prompt 3: logros a partir de tareas flojas',
        },
        {
          type: 'p',
          text: 'Cuando tus viñetas son “responsable de…” o “colaboración en…”. Prompt: Te paso viñetas débiles y, aparte, datos brutos (volumen, herramientas, frecuencia, equipo). Reescribe cada viñeta con verbo de acción. Si un dato no llega para un resultado, escribe el alcance sin inventar un KPI. Máximo 20 palabras por viñeta. Español de España, tono profesional, sin marketing.',
        },
        {
          type: 'p',
          text: 'Ejemplo de datos brutos que sí puedes pegar: “tienda familiar, verano 2023, caja y reposición, unos 25 clientes por turno, Excel para pedidos”. El modelo puede devolver: Actualicé pedidos en Excel y atendí unos 25 clientes por turno en temporada alta. Eso es defendible. “Aumenté las ventas un 40%” no lo es si no lo mediste.',
        },
        {
          type: 'h2',
          text: 'Prompt 4: CV sin experiencia (prácticas, proyectos, voluntariado)',
        },
        {
          type: 'p',
          text: 'Prompt: Soy candidato junior. Te paso estudios, proyectos, prácticas y trabajos informales. No disfraces un trabajo de clase como empleo remunerado. Orden: Resumen, Proyectos o experiencia relevante, Formación, Habilidades, Idiomas. Una página. Cada proyecto: problema, mi rol, herramientas, resultado (aunque sea académico). Señala qué quitarías si sobra espacio.',
        },
        {
          type: 'p',
          text: 'Pide después: “Propón 6 skills técnicas que sí se deducen de lo que te he dado, no una lista de moda (Python, SQL, Figma) si no aparecen en mis proyectos.” Esa frase corta el relleno que hunde los CV junior generados por IA.',
        },
        {
          type: 'h2',
          text: 'Prompt 5: versión en inglés',
        },
        {
          type: 'p',
          text: 'No traduzcas palabra a palabra. Prompt: Localiza este CV al inglés para una oferta que te pego. Conserva hechos y cifras. Usa títulos de puesto del mercado de destino, no calcos (becario no siempre es intern). Tono europeo profesional, no jerga de Silicon Valley si la oferta es local. Marca con [CHECK] cualquier título que hayas adaptado para que yo lo valide.',
        },
        {
          type: 'p',
          text: 'Luego compara fechas y empresas con la versión en español. Las inconsistencias entre idiomas parecen exageración en entrevista. Mantén una fuente de verdad (inventario) y genera ES y EN desde ahí.',
        },
        {
          type: 'h2',
          text: 'Cómo pegar la oferta (para que el prompt no alucine)',
        },
        {
          type: 'p',
          text: 'No pegues un pantallazo. Copia el texto del anuncio: título, requisitos, responsabilidades y stack. Recorta el bloque de “somos una empresa líder con valores” si no aporta keywords. Si el anuncio está en un portal con cookie wall, abre “mostrar más” antes de copiar. Un prompt excelente con una oferta a medias produce un CV a medias.',
        },
        {
          type: 'p',
          text: 'Si la oferta pide cinco años de Salesforce y tú usaste un CRM interno dos años, dilo en el inventario: “CRM interno diario, no Salesforce”. El modelo dejará de meter Salesforce en cada viñeta. Esa honestidad te ahorra una entrevista incómoda y un posible rechazo por mentir en el ATS.',
        },
        {
          type: 'h2',
          text: 'Checklist de 5 minutos después del prompt',
        },
        {
          type: 'ul',
          items: [
            '¿Puedo explicar cada cifra en una entrevista sin mirar el chat?',
            '¿Fechas y empresas coinciden con LinkedIn?',
            '¿El idioma es el del anuncio de punta a punta?',
            '¿He quitado proactivo, apasionado y “orientado a resultados”?',
            '¿El texto vive ya en un PDF seleccionable, no solo en el hilo?',
          ],
        },
        {
          type: 'h2',
          text: 'Ajustes por herramienta: ChatGPT, Gemini, Claude',
        },
        {
          type: 'p',
          text: 'ChatGPT: funciona bien en un hilo largo si pegas de nuevo la oferta cuando el modelo “olvida” y vuelve a los clichés. Activa un recordatorio cada pocos mensajes: “sigue sin inventar números”. No uses GPTs de terceros que prometan “CV ATS automático” sin ver el archivo final.',
        },
        {
          type: 'p',
          text: 'Gemini: útil si adjuntas el PDF; verifica la extracción (email, fechas, columnas). Si el adjunto sale incompleto, pega texto plano. Mismo prompt que en ChatGPT. Gemini a veces resume de más: añade “no acortes logros con métricas; conserva las cifras que yo di”.',
        },
        {
          type: 'p',
          text: 'Claude: suele cumplir mejor “no inventes” y “marca [FALTA]”. Úsalo para la pasada de escepticismo: pega el CV que te dio ChatGPT y pide “lista de afirmaciones no soportadas por el texto original”. Esa auditoría es oro antes de enviar.',
        },
        {
          type: 'ul',
          items: [
            'Un chat por oferta o por familia de rol; no mezcles diez anuncios.',
            'Pega la oferta cada vez que pidas una adaptación nueva.',
            'Guarda el inventario fuera del chat (notas o hoja). El hilo no es tu archivo maestro.',
            'Nunca pidas “inventa logros creíbles para cubrir este requisito”.',
          ],
        },
        {
          type: 'h2',
          text: 'Prompts que no debes usar',
        },
        {
          type: 'p',
          text: '“Hazme el mejor CV del mundo”, “actúa como reclutador de Google y dame un 98/100”, “mete keywords en blanco al final”, “infle un poco la seniority”. Eso produce texto que parece IA, a veces trampas detectables, y un documento que no puedes defender. Tampoco pidas un diseño Canva dentro del chat: te devolverá markdown o HTML frágil.',
        },
        {
          type: 'p',
          text: 'Si el modelo te da un CV “perfecto” en un mensaje, desconfía. Un buen resultado es un borrador con [FALTA], una lista de keywords y decisiones tuyas. Después, pasa el texto a un PDF parseable. En ClarCV subes el archivo, ves fricción de claridad y estructura, y regeneras con plantillas ATS. Empieza gratis; Pro a 7 €/mes. Español e inglés.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Puedo usar el mismo prompt en ChatGPT y en Gemini?',
              a: 'Sí. El prompt es el briefing. Lo que cambia es el texto que pegas debajo y, en Gemini, si el PDF se extrae entero.',
            },
            {
              q: '¿Cuántas keywords debo pedirle?',
              a: 'Entre 8 y 15 términos reales de la oferta. Más se convierte en relleno. Solo las que puedas defender.',
            },
            {
              q: '¿Y si no tengo métricas?',
              a: 'Pide alcance y frecuencia, no porcentajes inventados. Un proxy honesto supera un KPI falso.',
            },
            {
              q: '¿Estos prompts sustituyen a ClarCV?',
              a: 'No. Redactan texto. No generan el PDF ATS ni analizan el parseo. El chat es el borrador; el archivo que envías es otro paso.',
            },
            {
              q: '¿Debo pegar mi teléfono y email en el prompt?',
              a: 'No es necesario para redactar. Puedes poner [CONTACTO] y añadirlos en el documento final.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'ChatGPT, Gemini, and Claude prompts to write a CV',
      description:
        'Copy-paste prompts to improve a CV, tailor it to a job ad, rewrite achievements, and extract keywords—with rules so the model does not invent facts.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'A ChatGPT prompt for a CV is not a magic sentence. It is a brief: who you are, which posting, which rules, and which output format. If you only type “make me a résumé”, the model fills gaps with clichés and often with metrics that never happened. This guide is a pack to copy, paste, and edit.',
        },
        {
          type: 'p',
          text: 'It works in ChatGPT, Gemini, and Claude. Change little in the prompt text; change the context you paste underneath. Always: inventory or current CV + job ad as text + “do not invent”. The full method (inventory, review, ATS PDF) is in how to make a CV with ChatGPT. Here we focus on the instructions.',
        },
        {
          type: 'p',
          text: 'Before pasting anything into chatgpt.com, strip national ID, a full street address, and photos. If the goal is a file ready to send, the chat drafts; ClarCV formats and analyses. Prompts do not replace facts. If you mark [MISSING], fill it yourself—do not ask the model to “estimate a realistic percentage”.',
        },
        {
          type: 'h2',
          text: 'Rules that belong in every prompt',
        },
        {
          type: 'ul',
          items: [
            'Output language = language of the job ad (do not mix Spanish and English).',
            'Do not invent metrics, tools, titles, dates, or employers.',
            'If a fact is missing, mark [MISSING]; do not fill the gap.',
            'Bullets: verb + what + scope or tool + outcome if you have one.',
            'Banned: proactive, passionate, results-oriented, excellent communication skills, data-driven as decoration.',
            'Do not ask for layout, columns, icons, or a “premium visual CV”.',
          ],
        },
        {
          type: 'h2',
          text: 'Prompt 1: rewrite the CV you already have',
        },
        {
          type: 'p',
          text: 'Copy this, then paste your CV as text and, if you can, a typical target job ad. Prompt: You are a résumé editor. I will paste my current CV. Improve clarity and density. Keep dates, employers, and titles. Turn tasks into achievements only when I provided a result or a proxy. Remove clichés. One page if my career is short; otherwise say what you would cut. Structure: Summary (3–4 lines), Experience, Education, Skills, Languages. Return the CV only.',
        },
        {
          type: 'p',
          text: 'Second message in the same thread: Make a short table: original line → new line → why. If you added any number I did not give, list it separately. That table is your quality control. ChatGPT and Gemini often “improve” by inserting round figures; the table exposes them.',
        },
        {
          type: 'h2',
          text: 'Prompt 2: tailor the CV to a job ad (highest impact)',
        },
        {
          type: 'p',
          text: 'This prompt moves interviews because it forces alignment. Prompt: I will paste A) my CV B) a job posting. Extract 10 real keywords from the ad (tools, domain, responsibility verbs). Tell me which already appear in my CV and which are missing. Rewrite the summary, the five most relevant bullets, and the skills list using only keywords I can defend. Do not copy requirements I do not meet. Do not invent the ad’s flagship tool if I have not used it. Output: 1) keyword list 2) tailored CV 3) three remaining gaps and how I would address them honestly in interview.',
        },
        {
          type: 'p',
          text: 'If you apply to two role families (for example operations and customer support), do not reuse the same thread. Start a new chat per family. Mixing ads produces a Frankenstein that convinces no one.',
        },
        { type: 'cta', variant: 'upload' },
        {
          type: 'h2',
          text: 'Prompt 3: achievements from weak tasks',
        },
        {
          type: 'p',
          text: 'When your bullets are “responsible for…” or “collaborated on…”. Prompt: I will paste weak bullets and, separately, raw facts (volume, tools, frequency, team). Rewrite each bullet with an action verb. If a fact is not enough for an outcome, state scope without inventing a KPI. Max 20 words per bullet. Professional tone, no marketing.',
        },
        {
          type: 'p',
          text: 'Example of raw facts worth pasting: “family shop, summer 2023, till and restocking, about 25 customers per shift, Excel for orders”. The model can return: Updated Excel orders and served about 25 customers per shift in peak season. That is defensible. “Increased sales 40%” is not if you never measured it.',
        },
        {
          type: 'h2',
          text: 'Prompt 4: CV with little experience (projects, internships, volunteering)',
        },
        {
          type: 'p',
          text: 'Prompt: I am a junior candidate. I will paste studies, projects, internships, and informal work. Do not disguise classwork as paid employment. Order: Summary, Projects or relevant experience, Education, Skills, Languages. One page. Each project: problem, my role, tools, outcome (academic is fine). Flag what you would cut if space is tight.',
        },
        {
          type: 'p',
          text: 'Then ask: “Propose six technical skills that actually follow from what I gave you, not a trendy list (Python, SQL, Figma) if they are absent from my projects.” That line cuts the padding that sinks junior AI CVs.',
        },
        {
          type: 'h2',
          text: 'Prompt 5: English version',
        },
        {
          type: 'p',
          text: 'Do not translate word for word. Prompt: Localize this CV into English for the job ad I paste. Keep facts and figures. Use destination-market job titles, not calques. Professional European tone, not Silicon Valley jargon if the ad is local. Mark [CHECK] on any title you adapted so I can validate it.',
        },
        {
          type: 'p',
          text: 'Then compare dates and employers with the Spanish version. Inconsistencies across languages look like exaggeration in interview. Keep one source of truth (inventory) and generate ES and EN from it.',
        },
        {
          type: 'h2',
          text: 'How to paste the job ad (so the prompt does not hallucinate)',
        },
        {
          type: 'p',
          text: 'Do not paste a screenshot. Copy the ad text: title, requirements, responsibilities, and stack. Cut the “we are a values-led industry leader” block if it adds no keywords. If the posting sits behind a cookie wall, expand “show more” before copying. An excellent prompt with a half-copied ad produces a half-copied CV.',
        },
        {
          type: 'p',
          text: 'If the ad wants five years of Salesforce and you used an internal CRM for two, say so in the inventory: “daily internal CRM, not Salesforce”. The model will stop stuffing Salesforce into every bullet. That honesty saves an awkward interview and a possible ATS reject for lying.',
        },
        {
          type: 'h2',
          text: 'Five-minute checklist after the prompt',
        },
        {
          type: 'ul',
          items: [
            'Can I explain every figure in an interview without looking at the chat?',
            'Do dates and employers match LinkedIn?',
            'Is the language the ad’s language from top to bottom?',
            'Did I remove proactive, passionate, and “results-oriented”?',
            'Does the text already live in a selectable PDF, not only in the thread?',
          ],
        },
        {
          type: 'h2',
          text: 'Tweaks by tool: ChatGPT, Gemini, Claude',
        },
        {
          type: 'p',
          text: 'ChatGPT: works well in a long thread if you paste the ad again when the model “forgets” and returns to clichés. Remind it every few messages: “still do not invent numbers”. Skip third-party GPTs that promise an “automatic ATS CV” without you seeing the final file.',
        },
        {
          type: 'p',
          text: 'Gemini: useful if you attach the PDF; verify extraction (email, dates, columns). If the attachment is incomplete, paste plain text. Same prompt as ChatGPT. Gemini sometimes over-summarises: add “do not shorten bullets that contain metrics; keep the figures I gave”.',
        },
        {
          type: 'p',
          text: 'Claude: often follows “do not invent” and “mark [MISSING]” more tightly. Use it for the scepticism pass: paste the CV ChatGPT gave you and ask for “a list of claims not supported by the original text”. That audit is gold before you send.',
        },
        {
          type: 'ul',
          items: [
            'One chat per ad or role family; do not mix ten postings.',
            'Paste the ad every time you request a new tailoring.',
            'Keep the inventory outside the chat (notes or a sheet). The thread is not your master file.',
            'Never ask it to “invent believable achievements to cover this requirement”.',
          ],
        },
        {
          type: 'h2',
          text: 'Prompts you should not use',
        },
        {
          type: 'p',
          text: '“Make me the best CV in the world”, “act as a Google recruiter and give me 98/100”, “hide keywords in white at the bottom”, “inflate seniority a bit”. That yields AI-sounding text, sometimes detectable tricks, and a document you cannot defend. Do not ask for a Canva layout inside the chat either: you will get markdown or fragile HTML.',
        },
        {
          type: 'p',
          text: 'If the model hands you a “perfect” CV in one shot, be suspicious. A good result is a draft with [MISSING], a keyword list, and your decisions. Then move the text into a parseable PDF. On ClarCV you upload the file, see clarity and structure issues, and regenerate with ATS templates. Start free; Pro is €7/month. Spanish and English.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Can I use the same prompt in ChatGPT and Gemini?',
              a: 'Yes. The prompt is the brief. What changes is the text you paste underneath and, in Gemini, whether the PDF extracts fully.',
            },
            {
              q: 'How many keywords should I ask for?',
              a: 'Eight to fifteen real terms from the ad. More becomes stuffing. Only ones you can defend.',
            },
            {
              q: 'What if I have no metrics?',
              a: 'Ask for scope and frequency, not invented percentages. An honest proxy beats a fake KPI.',
            },
            {
              q: 'Do these prompts replace ClarCV?',
              a: 'No. They draft wording. They do not produce an ATS PDF or analyse parsing. The chat is the draft; the file you send is a separate step.',
            },
            {
              q: 'Should I paste my phone and email into the prompt?',
              a: 'Not required for drafting. Use [CONTACT] and add details in the final document.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'chatgpt-o-clarcv-para-tu-cv',
    date: '2026-08-17',
    category: 'ai',
    related: ['mejorar-cv-con-ia', 'privacidad-ia-cv', 'revisar-cv-online-ats'],
    es: {
      title: 'ChatGPT o una herramienta de CV: cuándo basta el chat',
      description:
        'Comparativa honesta: qué resuelve ChatGPT o Gemini, qué no (PDF ATS, plantillas, privacidad) y cuándo tiene sentido ClarCV a 7 €/mes.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'La pregunta no es “ChatGPT o ClarCV” como si fueran el mismo producto. ChatGPT (y Gemini, y Claude) es un chat de propósito general. ClarCV es un flujo para subir un CV, analizarlo y sacar un PDF con plantilla ATS. Se complementan. El fallo es usar el chat como si ya hubieras enviado el archivo.',
        },
        {
          type: 'p',
          text: 'Si buscas una alternativa a ChatGPT para el currículum, suele ser porque el texto queda genérico, no sabes cómo convertirlo en PDF serio, o no quieres pegar tu trayectoria en chatgpt.com. Esta guía delimita cuándo el chat basta, cuándo te frena, y qué hace una herramienta de CV con IA de forma distinta. Sin teatro de “la IA del futuro”: trabajo de envío de candidaturas.',
        },
        {
          type: 'p',
          text: 'Empieza gratis en clarcv.com si ya tienes un PDF o Word. Pro son 7 €/mes con precio claro. El chat de consumo, en cambio, parece gratis y te cobra en tiempo de edición, riesgo de inventos y, a veces, en datos que no deberías haber pegado.',
        },
        {
          type: 'h2',
          text: 'Lo que el chat resuelve bien',
        },
        {
          type: 'ul',
          items: [
            'Borrador de resumen y viñetas cuando tú aportas hechos e inventario.',
            'Traducción o localización ES↔EN con un prompt de “no calques”.',
            'Lista de keywords de una oferta y huecos respecto a tu texto.',
            'Brainstorm de verbos de acción a partir de tareas flojas.',
            'Una pasada de tono (menos marketing, más evidencia) si se lo pides en serio.',
          ],
        },
        {
          type: 'p',
          text: 'Para eso no necesitas pagar ClarCV el primer día. Necesitas un buen prompt y una revisión humana. El pack está en prompts de IA para CV. El método, en cómo hacer un CV con ChatGPT. Si tu único problema es “no sé cómo escribir esta viñeta”, el chat es suficiente.',
        },
        {
          type: 'h2',
          text: 'Lo que el chat no hace (aunque lo pidas)',
        },
        {
          type: 'p',
          text: 'No genera un PDF A4 con texto seleccionable y plantilla pensada para parsers. Devuelve markdown, listas o un “diseño” que al copiar a Canva se vuelve imagen. No comprueba si el email está en un encabezado gráfico. No sabe si tu archivo pesa demasiado para un portal. No alinea 18 plantillas profesionales al mismo contenido.',
        },
        {
          type: 'p',
          text: 'Tampoco ve tu archivo como lo ve un ATS. Tú pegas texto; el filtro de la empresa recibe un PDF. Esa distancia explica candidaturas que “en el chat se veían bien” y no generan respuesta. Una herramienta de CV trabaja sobre el documento, no solo sobre el párrafo.',
        },
        {
          type: 'ul',
          items: [
            'ChatGPT: redacta. No parsea tu PDF ni te dice qué sección se rompe al extraer.',
            'Gemini: puede leer un adjunto, con extracción imperfecta. Sigue sin exportar plantilla ATS.',
            'Claude: suele inventar menos si se lo prohibes. Sigue siendo texto, no archivo de envío.',
            'ClarCV: subes PDF o Word, análisis de claridad y estructura, regeneración con plantillas ATS a PDF.',
          ],
        },
        {
          type: 'h2',
          text: 'Privacidad: chatgpt.com frente a una API de empresa',
        },
        {
          type: 'p',
          text: 'En el ChatGPT de consumo pegas el CV en un producto generalista. Los ajustes de historial y entrenamiento dependen de tu cuenta. No hay un flujo de CV con política específica ni un DPA con ClarCV como responsable. Para un documento con teléfono, email y diez años de empleo, eso importa.',
        },
        {
          type: 'p',
          text: 'ClarCV no pega tu CV en chatgpt.com. Usa la API de OpenAI para tu petición (análisis o reescritura), con DPA, sin uso para entrenar modelos de propósito general, y sin venta de datos ni publicidad dirigida. Sí guarda lo necesario para el servicio (cuenta, archivo, resultados) mientras la cuenta esté activa. Detalle en qué pasa con tu CV cuando usas IA y en clarcv.com/privacy.',
        },
        {
          type: 'p',
          text: 'Ninguna opción es “cero servidores”. Elegir es elegir el marco: chat generalista o producto de CV con reglas dichas en claro. Si solo quieres una frase más elegante, anonimiza el contacto en el chat. Si vas a iterar y descargar, un flujo de producto reduce fricción y sorpresas.',
        },
        {
          type: 'h2',
          text: 'Un ejemplo concreto (misma persona, dos desenlaces)',
        },
        {
          type: 'p',
          text: 'Ana tiene un PDF de Canva y pide a ChatGPT “mejora mi CV para esta oferta de CSM”. El chat devuelve un resumen brillante con “reduje churn un 18%” que ella nunca midió, y una lista de skills copiada del anuncio (Gainsight incluido). Lo pega otra vez en Canva, exporta, envía. El ATS extrae mal la columna izquierda; el reclutador que sí abre el archivo duda de Gainsight en la entrevista. El chat no falló del todo: falló el sistema.',
        },
        {
          type: 'p',
          text: 'Mismo punto de partida con el flujo combinado: Ana pega oferta e inventario, el prompt marca [FALTA] en churn, ella escribe “40 cuentas en HubSpot, tiempo de respuesta de 12 a 5 horas”. Sube el PDF a ClarCV, cambia a una plantilla de una columna, descarga texto seleccionable. El ATS encuentra HubSpot y CSM. La entrevista habla de hechos que ella puede contar. Eso es la diferencia entre herramienta de chat y herramienta de CV.',
        },
        {
          type: 'h2',
          text: 'Coste real (no solo el precio de la suscripción)',
        },
        {
          type: 'p',
          text: 'ChatGPT Plus o Gemini Advanced no están pensados para ATS. Pagas (o usas el cupo gratuito) por un modelo general. El coste oculto es rehacer el diseño cada vez, cazar inventos y dudar si el PDF parsea. ClarCV Free cubre un primer uso; Pro a 7 €/mes tiene sentido si adaptas el CV a varias ofertas en el mismo mes.',
        },
        {
          type: 'p',
          text: 'Si haces una candidatura al año, el chat más una plantilla limpia puede bastar. Si estás en búsqueda activa, el cuello de botella no es “otro párrafo”, es velocidad con control: misma base, variantes por oferta, archivo fiable. Ahí el chat se convierte en un paso, no en el producto entero.',
        },
        {
          type: 'h2',
          text: 'Cómo usarlos juntos (el flujo que recomendamos)',
        },
        {
          type: 'p',
          text: '1) Inventario de hechos fuera del chat. 2) Prompt con oferta para un borrador de texto (ChatGPT, Gemini o Claude). 3) Cazar [FALTA], clichés y cifras no dadas. 4) Subir el PDF o Word a ClarCV: análisis + plantilla ATS + descarga. 5) Para la siguiente oferta, no empieces de cero: ajusta resumen y viñetas, vuelve a generar el archivo.',
        },
        {
          type: 'p',
          text: 'Eso no es “dejar ChatGPT”. Es no pedirle al chat el trabajo de un generador de documentos. El reclutador no ve tu hilo de Gemini. Ve un adjunto. Ese adjunto tiene que copiarse en un bloc de notas, mostrar fechas junto a puestos y no contradecir LinkedIn.',
        },
        {
          type: 'h2',
          text: 'Señales de que el chat ya no te basta',
        },
        {
          type: 'ul',
          items: [
            'El PDF de Canva no deja seleccionar texto, o el portal rechaza el archivo.',
            'Has enviado el mismo texto genérico a diez ofertas distintas.',
            'El modelo te ha metido herramientas o porcentajes que no puedes defender.',
            'No quieres seguir pegando el CV completo en un chat de consumo.',
            'Necesitas ES y EN con la misma base de hechos y un formato estable.',
          ],
        },
        {
          type: 'p',
          text: 'Si marcas dos o más, prueba ClarCV con el archivo que ya tienes. La IA del producto señala vaguedad y fricción estructural; las plantillas evitan el salto a un diseño que rompe el parseo. Empieza gratis. Si el análisis te resulta útil, Pro es 7 €/mes, no un “plan enterprise” opaco.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿ClarCV usa ChatGPT por detrás?',
              a: 'Usa la API de OpenAI para análisis y reescritura, no la web chatgpt.com. El marco de datos (DPA, sin entrenamiento de modelos generales) no es el del chat de consumo.',
            },
            {
              q: '¿Puedo hacer todo solo con Gemini?',
              a: 'Puedes redactar. El archivo ATS, las plantillas y el análisis del documento son otro trabajo. Gemini no sustituye ese paso.',
            },
            {
              q: '¿Es más barato ChatGPT Plus que ClarCV Pro?',
              a: 'Son productos distintos. Plus es un chat general. Pro (7 €/mes) es iterar CVs a PDF ATS. Si ya pagas Plus, igual te falta el archivo de envío.',
            },
            {
              q: '¿El chat no sirve para nada entonces?',
              a: 'Sirve mucho para el texto, con inventario y prompts estrictos. Falla como único sistema de candidatura.',
            },
            {
              q: '¿Debo dejar de usar ChatGPT si me registro en ClarCV?',
              a: 'No. Úsalo para brainstorm o una viñeta puntual. Sube a ClarCV cuando toque archivo, plantilla y análisis.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'ChatGPT vs a CV tool: when a chat is enough',
      description:
        'Honest comparison: what ChatGPT or Gemini can do, what they cannot (ATS PDF, templates, privacy), and when ClarCV at €7/month makes sense.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'The question is not “ChatGPT or ClarCV” as if they were the same product. ChatGPT (and Gemini, and Claude) is a general-purpose chat. ClarCV is a flow to upload a CV, analyse it, and export a PDF with an ATS template. They complement each other. The failure is treating the chat as if you had already sent the file.',
        },
        {
          type: 'p',
          text: 'If you want an alternative to ChatGPT for your résumé, it is usually because the wording is generic, you cannot turn it into a serious PDF, or you do not want to paste your career into chatgpt.com. This guide draws the line: when chat is enough, when it stalls you, and what a CV tool with AI does differently. No “future of AI” theatre: sending applications.',
        },
        {
          type: 'p',
          text: 'Start free at clarcv.com if you already have a PDF or Word file. Pro is €7/month with a clear price. Consumer chat looks free and charges you in editing time, invented facts, and sometimes in data you should not have pasted.',
        },
        {
          type: 'h2',
          text: 'What the chat handles well',
        },
        {
          type: 'ul',
          items: [
            'Drafting a summary and bullets when you supply facts and an inventory.',
            'ES↔EN localisation with a “no calques” prompt.',
            'A keyword list from a job ad and gaps versus your text.',
            'Action-verb brainstorming from weak tasks.',
            'A tone pass (less marketing, more evidence) if you insist on it.',
          ],
        },
        {
          type: 'p',
          text: 'You do not need to pay for ClarCV on day one for that. You need a good prompt and a human review. The pack is in AI prompts for CVs. The method is in how to make a CV with ChatGPT. If your only problem is “I do not know how to write this bullet”, the chat is enough.',
        },
        {
          type: 'h2',
          text: 'What the chat does not do (even if you ask)',
        },
        {
          type: 'p',
          text: 'It does not produce an A4 PDF with selectable text and a template built for parsers. It returns markdown, lists, or a “layout” that becomes an image when copied into Canva. It does not check whether your email lives only in a graphic header. It does not know if the file is too heavy for a portal. It does not fit the same content into 18 professional templates.',
        },
        {
          type: 'p',
          text: 'It also does not see your file the way an ATS does. You paste text; the employer filter receives a PDF. That gap explains applications that “looked fine in the chat” and get no reply. A CV tool works on the document, not only on the paragraph.',
        },
        {
          type: 'ul',
          items: [
            'ChatGPT: writes. It does not parse your PDF or tell you which section breaks on extraction.',
            'Gemini: can read an attachment, with imperfect extraction. Still no ATS template export.',
            'Claude: often invents less if you forbid it. Still text, not a sendable file.',
            'ClarCV: you upload PDF or Word, get clarity and structure analysis, regenerate with ATS templates to PDF.',
          ],
        },
        {
          type: 'h2',
          text: 'Privacy: chatgpt.com versus a business API',
        },
        {
          type: 'p',
          text: 'In consumer ChatGPT you paste the CV into a generalist product. History and training settings depend on your account. There is no CV-specific policy and no DPA with ClarCV as controller. For a document with phone, email, and ten years of work, that matters.',
        },
        {
          type: 'p',
          text: 'ClarCV does not paste your CV into chatgpt.com. It uses OpenAI’s API for your request (analysis or rewrite), with a DPA, without using the content to train general-purpose models, and without selling data or running targeted ads. It does store what the service needs (account, file, results) while the account is active. Detail in what happens to your CV when you use AI and at clarcv.com/privacy.',
        },
        {
          type: 'p',
          text: 'No option is “zero servers”. The choice is the framework: a generalist chat or a CV product with stated rules. If you only want a nicer sentence, anonymise contact details in the chat. If you will iterate and download, a product flow reduces friction and surprises.',
        },
        {
          type: 'h2',
          text: 'A concrete example (same person, two outcomes)',
        },
        {
          type: 'p',
          text: 'Ana has a Canva PDF and asks ChatGPT to “improve my CV for this CSM role”. The chat returns a glossy summary with “reduced churn 18%” she never measured, and a skills list copied from the ad (Gainsight included). She pastes it back into Canva, exports, sends. The ATS mangles the left column; the recruiter who does open the file doubts Gainsight in the interview. The chat did not fully fail: the system did.',
        },
        {
          type: 'p',
          text: 'Same starting point with the combined flow: Ana pastes the ad and inventory, the prompt marks [MISSING] on churn, she writes “40 HubSpot accounts, response time 12 hours to 5”. She uploads the PDF to ClarCV, switches to a single-column template, downloads selectable text. The ATS finds HubSpot and CSM. The interview is about facts she can tell. That is the difference between a chat tool and a CV tool.',
        },
        {
          type: 'h2',
          text: 'Real cost (not only the subscription price)',
        },
        {
          type: 'p',
          text: 'ChatGPT Plus or Gemini Advanced are not built for ATS. You pay (or use a free quota) for a general model. The hidden cost is rebuilding layout every time, catching inventions, and wondering whether the PDF parses. ClarCV Free covers a first pass; Pro at €7/month makes sense if you tailor the CV to several ads in the same month.',
        },
        {
          type: 'p',
          text: 'If you apply once a year, chat plus a clean template can be enough. If you are in an active search, the bottleneck is not “another paragraph”; it is speed with control: one base, variants per ad, a reliable file. Then the chat becomes a step, not the whole product.',
        },
        {
          type: 'h2',
          text: 'How to use them together (the flow we recommend)',
        },
        {
          type: 'p',
          text: '1) Fact inventory outside the chat. 2) Prompt with the ad for a text draft (ChatGPT, Gemini, or Claude). 3) Catch [MISSING], clichés, and figures you never supplied. 4) Upload the PDF or Word to ClarCV: analysis + ATS template + download. 5) For the next ad, do not start over: adjust summary and bullets, generate the file again.',
        },
        {
          type: 'p',
          text: 'That is not “giving up ChatGPT”. It is not asking a chat to do a document generator’s job. The recruiter does not see your Gemini thread. They see an attachment. That attachment must paste into Notepad, show dates next to roles, and not contradict LinkedIn.',
        },
        {
          type: 'h2',
          text: 'Signs the chat is no longer enough',
        },
        {
          type: 'ul',
          items: [
            'The Canva PDF will not let you select text, or the portal rejects the file.',
            'You have sent the same generic wording to ten different ads.',
            'The model inserted tools or percentages you cannot defend.',
            'You do not want to keep pasting a full CV into a consumer chat.',
            'You need ES and EN from the same facts with a stable layout.',
          ],
        },
        {
          type: 'p',
          text: 'If two or more apply, try ClarCV with the file you already have. The product AI flags vague wording and structural friction; templates avoid jumping to a layout that breaks parsing. Start free. If the analysis helps, Pro is €7/month—not an opaque enterprise plan.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does ClarCV use ChatGPT under the hood?',
              a: 'It uses OpenAI’s API for analysis and rewriting, not the chatgpt.com website. The data framework (DPA, no general-model training) is not the consumer chat’s.',
            },
            {
              q: 'Can I do everything with Gemini alone?',
              a: 'You can draft. The ATS file, templates, and document analysis are a different job. Gemini does not replace that step.',
            },
            {
              q: 'Is ChatGPT Plus cheaper than ClarCV Pro?',
              a: 'They are different products. Plus is a general chat. Pro (€7/month) is iterating CVs to ATS PDFs. If you already pay for Plus, you may still lack the sendable file.',
            },
            {
              q: 'So the chat is useless?',
              a: 'It is very useful for wording, with an inventory and strict prompts. It fails as your only application system.',
            },
            {
              q: 'Should I stop using ChatGPT if I sign up for ClarCV?',
              a: 'No. Use it for brainstorming or a single bullet. Upload to ClarCV when you need the file, template, and analysis.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
